/* eslint-disable react/prop-types */
import { Container } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { useSelector, useDispatch } from 'react-redux';
import { withTranslation } from 'react-i18next';
import useQsParams from '../../../Components/Hooks/QueryString';
import {
  getCreditsCardsData,
  getCreditsChartsData,
  getOrdersFilters,
} from '../../../slices/manager-panel/thunk';
import React, { useEffect, useState } from 'react';

import { createSelector } from 'reselect';
import { getFilters } from '../../../Components/Common/Filter';
import { isEmpty } from 'lodash';
import CommunicationFilter from '../components/CommunicationFilter';
import NavTabRequest from '../../../Components/Common/NavTabRequest';
import Summary from './TabsContent/Summary';
import TotalCredits from './TabsContent/TotalCredits';
import CashbackCredits from './TabsContent/CashbackCredits';
import IncentiveCredits from './TabsContent/IncentiveCredits';
import AvailableCredits from './TabsContent/AvailableCredits';
import chartLocales from '../utils/locales';
import { formatCurrency } from '../../../helpers/format';

const CreditsSummary = ({ t }) => {
  const dispatch = useDispatch();
  const { searchParams } = useQsParams();
  const [, setFiltersParams] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [type, setType] = useState(searchParams.get('type'));

  document.title = `${t('results-manager.heading')} | Opencashback`;

  const filters = useSelector((state) => state.ManagerPanel.filters);

  useEffect(() => {
    dispatch(getOrdersFilters());
  }, [dispatch]);

  const { credits } = useSelector(
    createSelector(
      (state) => state.ManagerPanel,
      ({ credits }) => ({ credits })
    )
  );

  useEffect(() => {
    getData();
  }, [dispatch, searchParams.toString()]);

  useEffect(() => {
    const newType = searchParams.get('type');
    setType(newType);
  }, [searchParams]);

  useEffect(() => {
    type &&
      setChartOptions({
        chart: {
          toolbar: { show: false },
          ...chartLocales,
        },
        legend: {
          onItemHover: { highlightDataSeries: true },
          onItemClick: { toggleDataSeries: true },
        },
        markers: { size: 5 },
        stroke: { width: [1, 1, 1] },
        colors: ['#0e93c2', '#aaff00', '#ff6400'],
        labels: credits?.chart?.data?.map((i) => i?.date),
        xaxis: {
          labels: {
            formatter: (v) => v,
          },
        },
        yaxis: [
          {
            labels: {
              formatter: (v) => {
                return formatCurrency(v);
              },
            },
            tickAmount: 10,
          },
        ],
        dataLabels: {
          enabled: false,
          style: {
            colors: ['#ffffff', '#ffffff', '#ffffff'],
          },
          formatter: (v) => {
            return v > 0 ? v : '';
          },
          background: {
            enabled: true,
            padding: 4,
            borderRadius: 2,
            borderWidth: 1,
            borderColor: '#fff',
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ series, dataPointIndex, w }) => {
            const value1 = series[0][dataPointIndex] || 0;
            const value2 = series[1][dataPointIndex] || 0;
            const value3 = series[2][dataPointIndex] || 0;
            return `
            <div class="" style="padding: 10px; background: rgba(255, 255, 255, 0.96); border: 1px solid #e3e3e3; box-shadow: 2px 2px 6px -4px #999; border-radius: 5px; font-size: 14px; color: #333;">
              <div class="" style="font-weight: 600; margin-bottom: 5px;">
                ${w.globals.labels[dataPointIndex]}
              </div>
              <div class="" style="display: flex; align-items: center; margin-bottom: 5px;">
                <span class="" style="background-color: #0e93c2; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px;"></span>
                <span>
                  ${type == 'available' ? 'Total:' : 'Gerado:'}
                  <span style="font-weight: 500;"> ${formatCurrency(value1)}</span>
                </span>
              </div>
              <div class="" style="display: flex; align-items: center; margin-bottom: 5px;">
                <span class="" style="background-color: #aaff00; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px;"></span>
                <span>
                  ${type == 'available' ? 'Cashback:' : 'Disponibilizado:'}
                  <span style="font-weight: 500;">${formatCurrency(value2)}</span>
                </span>
              </div>
              <div class="" style="display: flex; align-items: center; margin-bottom: 5px;">
                <span class="" style="background-color: #ff6400; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px;"></span>
                <span>
                  ${type == 'available' ? 'Incentivo:' : 'Expirado:'}
                  <span style="font-weight: 500;">${formatCurrency(value3)}</span>
                </span>
              </div>
            </div>
          `;
          },
        },
      });
  }, [type, credits]);

  const filtersSummary = [{ field: 'created_at' }];

  const getData = () => {
    const type = searchParams.get('type');
    const params = type ? { type } : {};

    const appliedFilters = getFilters(filtersSummary, [
      { field: 'created_at', type: 'between_date' },
    ]);

    if (!isEmpty(appliedFilters)) {
      params.filters = appliedFilters;
    }

    setFiltersParams(params.filters);
    dispatch(getCreditsCardsData(params));
    dispatch(getCreditsChartsData(params));
  };

  const buildUrlWithFilters = (baseUrl) => {
    const url = new URL(baseUrl, window.location.origin);
    const appliedFilters = [
      { field: 'created_at', value: searchParams.get('created_at') },
      { field: 'channel', value: searchParams.get('channel') },
      { field: 'store_id', value: searchParams.get('store_id') },
    ];

    appliedFilters.forEach(({ field, value }) => {
      if (value) {
        url.searchParams.append(field, value);
      }
    });
    return url.toString();
  };

  const [activeTab, setActiveTab] = useState('1');

  const tabs = [
    {
      index: '1',
      label: t('results-manager.credits-summary.titles.summary'),
      content: (
        <Summary
          credits={credits}
          setActiveTab={setActiveTab}
          buildUrlWithFilters={buildUrlWithFilters}
        />
      ),
      isActive: !searchParams.get('type'),
      value: {},
    },
    {
      index: '2',
      label: t('results-manager.credits-summary.titles.total-credits'),
      content: (
        <TotalCredits
          credits={credits}
          chartOptions={chartOptions}
          type={type}
          buildUrlWithFilters={buildUrlWithFilters}
        />
      ),
      isActive: searchParams.get('total'),
      value: { type: 'total' },
    },
    {
      index: '3',
      label: t('results-manager.credits-summary.titles.cashback-credits'),
      content: (
        <CashbackCredits
          credits={credits}
          chartOptions={chartOptions}
          type={type}
          buildUrlWithFilters={buildUrlWithFilters}
        />
      ),
      isActive: searchParams.get('order'),
      value: { type: 'order' },
    },
    {
      index: '4',
      label: t('results-manager.credits-summary.titles.incentive-credits'),
      content: (
        <IncentiveCredits
          credits={credits}
          chartOptions={chartOptions}
          type={type}
          buildUrlWithFilters={buildUrlWithFilters}
        />
      ),
      isActive: searchParams.get('incentive'),
      value: { type: 'incentive' },
    },
    {
      index: '5',
      label: t('results-manager.credits-summary.titles.available-credits'),
      content: (
        <AvailableCredits
          credits={credits}
          chartOptions={chartOptions}
          type={type}
          buildUrlWithFilters={buildUrlWithFilters}
        />
      ),
      isActive: searchParams.get('available'),
      value: { type: 'available' },
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          isLoading={filters.isLoading}
          title={t('results-manager.credits-summary.title')}
          pageTitle={t('results-manager.heading')}
        />

        <CommunicationFilter
          filters={filters.data}
          activeTab={activeTab}
          legendDate={t('results-manager.credits-summary.date-title')}
        />

        <NavTabRequest
          items={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </Container>
    </div>
  );
};

export default withTranslation()(CreditsSummary);
